/* eslint-disable react-hooks/exhaustive-deps */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useState,
    useRef,
} from 'react';
import { fetchRequest, FetchResponse } from '../FetchRequest';
import { useNavigate } from 'react-router-dom';
import { ValidationData } from '../forms/FormValidationHelpers';

export enum RequestStatus {
    NEVER_EXECUTED = 'neverExecuted',
    PENDING = 'pending',
    ERROR = 'error',
    SUCCESS = 'success',
}

export const DefaultServerHeadersContext = createContext<{
    [key: string]: string;
}>({});

/* in body when throwing ApiException in backend */
export interface GenericRequestError {
    code: number;
    message: string;
}

export interface ConstraintViolationResponse {
    code: number;
    message: string;
    validationErrors: ValidationError[];
}

export interface ValidationError {
    message: string;
    field: string;
    violation: string;
    invalidValue: unknown;
    annotation: string;
    constraint: { [key: string]: unknown };
    messageTemplate: string;
}

export type ErrorResponseTypes =
    | ConstraintViolationResponse
    | GenericRequestError
    | ValidationData
    | null;

export type ServerRequestState<D, ED = ErrorResponseTypes> =
    | NeverExecutedState
    | PendingState<D>
    | SuccessState<D>
    | ErrorState<ED>;

export interface NeverExecutedState {
    readonly status: RequestStatus.NEVER_EXECUTED;
    readonly data: null;
}

export interface PendingState<D> {
    readonly status: RequestStatus.PENDING;
    readonly data: D | null;
}

export interface SuccessState<D> {
    readonly status: RequestStatus.SUCCESS;
    readonly data: D;
}

export interface ErrorState<ED> {
    readonly status: RequestStatus.ERROR;
    readonly data: ED | null;
    readonly httpStatusCode: number;
}

export type ServerWriteState<D, ED = object, P = undefined> = [
    ServerRequestState<D, ED>,
    (args?: P) => void,
    () => void,
];

export type WriteInfo<P> = (payload: P) => WriteRequestConfig;

/**
 * Gives you a function to send data to the backend. The send has to be triggered manually.
 * All requests are JSON, the default method is POST.
 *
 * @param writeInfoFactory This function gets the payload so you can build your RequestConfig accordingly.
 * @returns [state, send(payload), reset()]
 * @example A simple component that writes a new user
 *
 * function SomeComponent() {
 *     const [state, write] = useServerWrite<{ name: string }, { id: number }>(
 *         () => ({ url: `/record` })
 *     );
 *
 *     if (state.status === RequestStatus.NEVER_EXECUTED) {
 *         return <button onClick={() => write({ name: 'John Doe' })} />;
 *     } else if (state.status === RequestStatus.PENDING) {
 *         return <div>Spinner</div>;
 *     } else if (state.status === RequestStatus.ERROR) {
 *         return <div>Something went wrong</div>;
 *     } else {
 *         return <div>Created with ID: {state.data.id}</div>;
 *     }
 * }
 */
export function useServerWrite<Payload, Data, ErrorData = object>(
    writeInfoFactory: WriteInfo<Payload>,
): ServerWriteState<Data, ErrorData, Payload> {
    const defaultServerHeaders = useContext(DefaultServerHeadersContext);
    // Lets write stay stable. A layout effect, so that effects calling write
    // in the same commit already see this render's factory and headers.
    const latestFactory = useRef(writeInfoFactory);
    const latestHeaders = useRef(defaultServerHeaders);
    useLayoutEffect(() => {
        latestFactory.current = writeInfoFactory;
        latestHeaders.current = defaultServerHeaders;
    });
    const [state, setState] = useState<ServerRequestState<Data, ErrorData>>({
        data: null,
        status: RequestStatus.NEVER_EXECUTED,
    });

    const reset = useCallback(() => {
        setState({ data: null, status: RequestStatus.NEVER_EXECUTED });
    }, []);

    const write = useCallback((args?: Payload) => {
        const conf = latestFactory.current(args!);
        setState({ data: null, status: RequestStatus.PENDING });

        void fetchRequest(
            {
                ...conf,
                method: conf.method ?? RequestMethod.POST,
                body: args,
            },
            undefined,
            latestHeaders.current,
        ).then(response => {
            if (response !== null) {
                setState(responseState<Data, ErrorData>(response));
            }
        });
    }, []);

    return [state, write, reset];
}

export enum RequestMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE',
}

export interface RequestConfig extends WriteRequestConfig {
    body?: unknown;
}

export interface WriteRequestConfig {
    url: string;
    method?: RequestMethod;
    headers?: { [key: string]: string };
}

export type ServerFetchState<D, ED = D> = [
    ServerRequestState<D, ED>,
    () => void,
];
export type FetchInfo<C> = (context: C) => RequestConfig;

/**
 * Fetches and updates the server data on every context change.
 * If context is `null` no call is made.
 * If a request is running it will be canceled.
 * All requests are JSON, the default method is GET.
 *
 * @param requestInfoFactory This function gets the context so you can build your RequestConfig accordingly.
 * @param context If `null` no call is made. Otherwise a new request is made if any context data changes.
 * @returns [state, refetchFunction]
 * @example A simple component that fetches a record
 *
 * function SomeComponent({id}: {id: number}) {
 *     const [state] = useServerFetch<{name: string}, {id: number}>(
 *         context => ({url: `/record/${context.id}`}),
 *         {id}
 *     );
 *
 *     if (state.status === RequestStatus.PENDING || state.status === RequestStatus.NEVER_EXECUTED) {
 *         return <div>Spinner</div>
 *     } else if (state.status === RequestStatus.ERROR) {
 *         return <div>Something went wrong</div>
 *     } else {
 *         return <div>Hello {state.data.name}</div>
 *     }
 * }
 */
export function useServerFetch<Data, Context extends object, ErrorData = Data>(
    requestInfoFactory: FetchInfo<Context>,
    context: Context | null,
): ServerFetchState<Data, ErrorData> {
    const defaultServerHeaders = useContext(DefaultServerHeadersContext);
    const [forceFetchCount, setForceFetchCount] = useState(0);
    const [state, setState] = useState<ServerRequestState<Data, ErrorData>>({
        data: null,
        status: RequestStatus.NEVER_EXECUTED,
    });

    const refetchSameContext = useCallback(() => {
        setForceFetchCount(prev => prev + 1);
    }, []);

    useEffect(() => {
        if (context === null) {
            setState({ data: null, status: RequestStatus.NEVER_EXECUTED });
            return;
        }

        const conf = requestInfoFactory(context);
        const controller = new AbortController();
        setState(prev => ({
            data: prev.status !== RequestStatus.ERROR ? prev.data : null,
            status: RequestStatus.PENDING,
        }));
        void fetchRequest(
            { ...conf, method: conf.method ?? RequestMethod.GET },
            controller.signal,
            defaultServerHeaders,
        ).then(response => {
            if (response !== null && !controller.signal.aborted) {
                setState(responseState<Data, ErrorData>(response));
            }
        });

        return () => controller.abort();
    }, [
        JSON.stringify(context), // Compare context values, not the factory or object identity.
        forceFetchCount,
        defaultServerHeaders,
    ]);

    return [state, refetchSameContext];
}

function responseState<D, ED>(
    response: FetchResponse,
): ServerRequestState<D, ED> {
    if (response.status >= 200 && response.status < 300) {
        return {
            data: response.body as D,
            status: RequestStatus.SUCCESS,
        };
    }
    return {
        data: response.body as ED | null,
        status: RequestStatus.ERROR,
        httpStatusCode: response.status,
    };
}

/**
 * Will call the action function with the request data once every time it's successful.
 * WARNING: It doesn't take any dependencies so don't use outside data that will not get updated.
 * @param state RequestState
 * @param action The action to be executed onSuccess - will receive the success data
 */
export function useServerSuccessEffect<Data, ErrorData = Data>(
    state: ServerRequestState<Data, ErrorData>,
    action: (data: Data) => void | (() => void),
) {
    useEffect(() => {
        if (state.status === RequestStatus.SUCCESS) {
            const cleanupFunction = action(state.data);
            if (cleanupFunction) {
                return cleanupFunction;
            }
        }
    }, [state]);
}

/**
 * Will use the react-router history.push method to navigate every time it's successful.
 * @param state RequestState
 * @param to the path to navigate to, it can be a function getting as input the returned data
 */
export function useNavigateOnSuccess<Data, ErrorData = Data>(
    state: ServerRequestState<Data, ErrorData>,
    to: string | ((data: Data) => string),
    replace?: boolean,
) {
    const navigate = useNavigate();
    useServerSuccessEffect(state, data => {
        const target = typeof to === 'string' ? to : to(data);

        if (replace) {
            navigate(target, { replace: true });
        } else {
            navigate(target);
        }
    });
}

/**
 * Will call the action function with the request data once every time it's failed.
 * WARNING: It doesn't take any dependencies so don't use outside data that will not get updated.
 * @param state RequestState
 * @param action The action to be executed onError - will receive the error data
 */
export function useServerErrorEffect<D, ED = D>(
    state: ServerRequestState<D, ED>,
    action: (statusCode: number, data: ED | null) => void,
) {
    useEffect(() => {
        if (state.status === RequestStatus.ERROR) {
            action(state.httpStatusCode, state.data);
        }
    }, [state]);
}

/**
 * Will use the react-router history.push method to navigate every time it's failed.
 * @param state RequestState
 * @param to the path to navigate to
 */
export function useNavigateOnError<Data, ErrorData = Data>(
    state: ServerRequestState<Data, ErrorData>,
    to: string,
) {
    const navigate = useNavigate();
    useServerErrorEffect(state, () => {
        navigate(to);
    });
}
